export class NavDrag {
    constructor(selector, options = {}) {
        this.slider = typeof selector === 'string'
            ? document.querySelector(selector)
            : selector;

        if (!this.slider) return;

        this.container = this.slider.querySelector('.jsNavScrollContainer');
        if (!this.container) return;

        // Options
        this.options = Object.assign({ dragThreshold: 8 }, options);

        // Event system
        this.events = {};

        if (this.options.on && typeof this.options.on === 'object') {
            Object.entries(this.options.on).forEach(([event, handler]) => {
                this.on(event, handler);
            });
        }

        // State
        this.state = {
            isDown: false,
            startX: 0,
            currentTranslate: 0,
            prevTranslate: 0,
            isDragging: false,
            moved: 0,
            maxTranslate: 0,
            minTranslate: 0,
        };

        this._suppressClick = false;

        // Bind handlers
        this._onResize = this._onResize.bind(this);
        this._onMouseDown = this._onMouseDown.bind(this);
        this._onMouseMove = this._onMouseMove.bind(this);
        this._onMouseUp = this._onMouseUp.bind(this);
        this._onTouchStart = this._onTouchStart.bind(this);
        this._onTouchMove = this._onTouchMove.bind(this);
        this._onTouchEnd = this._onTouchEnd.bind(this);
        this._onClickCapture = this._onClickCapture.bind(this);

        // Setup slider immediately
        this._setup();

        this.emit('init');
    }

    /* ---------------- Event System ---------------- */
    on(event, callback) {
        if (!this.events[event]) this.events[event] = [];
        this.events[event].push(callback);
        return this;
    }

    off(event, callback) {
        if (!this.events[event]) return this;
        this.events[event] = this.events[event].filter(cb => cb !== callback);
        return this;
    }

    emit(event, ...args) {
        if (!this.events[event]) return;
        this.events[event].forEach(cb => cb.apply(this, args));
    }

    /* ---------------- Setup ---------------- */
    _setup() {
        this._updateLimits();
        this._updateDraggableClass();
        this._updateHasDragClass();

        window.addEventListener('resize', this._onResize);

        this.slider.addEventListener('mousedown', this._onMouseDown);
        document.addEventListener('mousemove', this._onMouseMove);
        document.addEventListener('mouseup', this._onMouseUp);

        this.slider.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this.slider.addEventListener('touchmove', this._onTouchMove, { passive: false });
        this.slider.addEventListener('touchend', this._onTouchEnd);

        // 👇 CLICK SUPPRESSION (capture phase)
        this.slider.addEventListener('click', this._onClickCapture, true);
    }

    /* ---------------- Click Suppression ---------------- */
    _onClickCapture(e) {
        if (this._suppressClick) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this._suppressClick = false;
        }
    }

    /* ---------------- Helpers ---------------- */
    _updateLimits() {
        this.state.maxTranslate = Math.min(
            0,
            this.container.offsetWidth - this.container.scrollWidth
        );
        this.state.minTranslate = 0;
    }

    _updateDraggableClass() {
        const isDraggable = this.container.scrollWidth > this.container.offsetWidth;
        this.slider.classList.toggle('is-draggable', isDraggable);
    }

    _updateHasDragClass() {
        this.slider.classList.toggle('has-drag', this.state.prevTranslate !== 0);
    }

    /* ---------------- Resize ---------------- */
    _onResize() {
        Object.assign(this.state, {
            isDown: false,
            startX: 0,
            currentTranslate: 0,
            isDragging: false,
            moved: 0,
        });

        this.container.style.transform = '';
        this._updateLimits();
        this._updateDraggableClass();
        this._updateHasDragClass();

        this.emit('resize');
    }

    /* ---------------- Mouse Drag ---------------- */
    _onMouseDown(e) {
        this.state.isDown = true;
        this.state.startX = e.pageX;
        this.state.isDragging = false;
        this.state.moved = 0;
        this.slider.classList.add('is-dragging');

        this.emit('dragStart', e);
    }

    _onMouseMove(e) {
        if (!this.state.isDown) return;

        const dx = e.pageX - this.state.startX;
        this.state.moved = dx;

        if (Math.abs(dx) > this.options.dragThreshold)
            this.state.isDragging = true;

        if (this.state.isDragging) {
            this.state.currentTranslate =
                this.state.prevTranslate + dx;

            this.state.currentTranslate = Math.max(
                this.state.maxTranslate,
                Math.min(this.state.currentTranslate, this.state.minTranslate)
            );

            this.container.style.transform =
                `translateX(${this.state.currentTranslate}px)`;

            this.emit('dragMove', dx);
        }
    }

    _onMouseUp() {
        if (this.state.isDragging) {
            this.state.prevTranslate = this.state.currentTranslate;
            this._suppressClick = true; // 👈 prevent click
        }

        this.state.isDown = false;
        this.state.isDragging = false;
        this.slider.classList.remove('is-dragging');
        this._updateHasDragClass();

        this.emit('dragEnd');
    }

    /* ---------------- Touch Drag ---------------- */
    _onTouchStart(e) {
        this.state.startX = e.touches[0].pageX;
        this.state.isDragging = false;
        this.state.moved = 0;
        this.slider.classList.add('is-dragging');

        this.emit('dragStart', e);
    }

    _onTouchMove(e) {
        const dx = e.touches[0].pageX - this.state.startX;
        this.state.moved = dx;

        if (Math.abs(dx) > this.options.dragThreshold)
            this.state.isDragging = true;

        if (this.state.isDragging) {
            if (e.cancelable) e.preventDefault();

            this.state.currentTranslate =
                this.state.prevTranslate + dx;

            this.state.currentTranslate = Math.max(
                this.state.maxTranslate,
                Math.min(this.state.currentTranslate, this.state.minTranslate)
            );

            this.container.style.transform =
                `translateX(${this.state.currentTranslate}px)`;

            this.emit('dragMove', dx);
        }
    }

    _onTouchEnd() {
        if (this.state.isDragging) {
            this.state.prevTranslate = this.state.currentTranslate;
            this._suppressClick = true; // 👈 prevent click
        }

        this.state.isDragging = false;
        this.slider.classList.remove('is-dragging');
        this._updateHasDragClass();

        this.emit('dragEnd');
    }

    /* ---------------- Destroy ---------------- */
    destroy() {
        window.removeEventListener('resize', this._onResize);
        this.slider.removeEventListener('mousedown', this._onMouseDown);
        document.removeEventListener('mousemove', this._onMouseMove);
        document.removeEventListener('mouseup', this._onMouseUp);
        this.slider.removeEventListener('touchstart', this._onTouchStart);
        this.slider.removeEventListener('touchmove', this._onTouchMove);
        this.slider.removeEventListener('touchend', this._onTouchEnd);
        this.slider.removeEventListener('click', this._onClickCapture, true);

        this.slider.classList.remove('is-dragging', 'is-draggable', 'has-drag');
        this.container.style.transform = '';

        this.emit('destroy');
    }

    reset() {
        Object.assign(this.state, {
            isDown: false,
            startX: 0,
            currentTranslate: 0,
            isDragging: false,
            moved: 0,
        });

        this.container.style.transform = '';
        this._updateLimits();
        this._updateDraggableClass();
        this._updateHasDragClass();

        this.emit('reset');
    }
}
